import { Button, Flex, HStack, Portal, Text } from '@chakra-ui/react'
import { MoreVertDots } from '@components/icons'
import { useDailyEvent } from '@daily-co/daily-react'
import { BlueBLogo } from '@images'
import { formatTimeFromSeconds } from '@utilities'
import React, { useEffect, useState } from 'react'
import {
  DialogBody,
  DialogContainer,
  DialogHeader
} from '../../../components/dialog'
import {
  DailyCameraButton,
  DailyMicButton,
  EndSessionButton,
  ParticipantsButton,
  TelehealthSettingsButton
} from '../../footer'
import {
  useActiveTelehealthSession,
  useTelehealthRecordingTimer
} from '../context/active-telehealth-session.context'
import { usePatientDailyManager } from '../context/patient-daily-manager.context'
import { DeviceSettingsModal } from './device-settings-modal'

export const ClientTelehealthFooter = () => {
  const { isInCall, leaveCall, isReadyToJoin, setIsReadyToJoin } =
    usePatientDailyManager()
  const { toggleSidebar, activeSidebarTab } = useActiveTelehealthSession()

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isLeaveCallOpen, setIsLeaveCallOpen] = useState(false)

  const closeLeaveCall = () => {
    setIsLeaveCallOpen(false)
  }

  const promptLeaveCall = () => {
    setIsLeaveCallOpen(true)
  }

  const handleLeaveCall = () => {
    setIsReadyToJoin(false)
    leaveCall()
    closeLeaveCall()
  }

  return (
    <Portal>
      <Flex
        position="fixed"
        bottom="0"
        h="60px"
        w="100%"
        bg="white"
        justify={{ base: 'center', sm: 'space-between' }}
        align="center"
        px="small"
        zIndex={2}
      >
        <Flex
          display={{ base: 'none', sm: 'flex' }}
          h="100%"
          justify="flex-start"
          align="center"
          minWidth="170px"
          maxWidth="300px"
        >
          <BlueBLogo ml="8px" mr="20px" w="19px" h="24px" />
          <Text alignSelf="center" fontSize="14px" fontWeight="bold">
            Session
          </Text>
          <TelehealthTimer />
        </Flex>
        <HStack gap="0px">
          {(isInCall || isReadyToJoin) && (
            <>
              <DailyCameraButton />
              <DailyMicButton />
              <TelehealthSettingsButton
                icon={<MoreVertDots />}
                onClick={() => setIsSettingsOpen(true)}
              />
              <EndSessionButton text="Leave" onClick={promptLeaveCall} />
            </>
          )}
        </HStack>
        <Flex
          h="100%"
          w={{ base: 'auto', sm: '170px' }}
          justify="flex-end"
          align="center"
        >
          {isInCall ? (
            <ParticipantsButton
              isActive={activeSidebarTab === 'participants'}
              onClick={() => {
                toggleSidebar('participants')
              }}
            />
          ) : null}
        </Flex>
      </Flex>
      <DeviceSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <DialogContainer
        size="xs"
        isOpen={isLeaveCallOpen}
        onClose={closeLeaveCall}
      >
        <DialogHeader text="Are you sure you want to leave this call?" />
        <DialogBody>
          <Flex direction="column" align="center" justify="center" gap="xsmall">
            <Button
              size="lg"
              isFullWidth
              onClick={
                isInCall ? handleLeaveCall : () => window.location.reload()
              }
              data-click-text="Yes, leave call"
            >
              Yes
            </Button>
            <Button
              variant="outline"
              size="lg"
              isFullWidth
              onClick={closeLeaveCall}
              data-click-text="Not yet, stay in call"
            >
              Not Yet
            </Button>
          </Flex>
        </DialogBody>
      </DialogContainer>
      <TimerManager />
    </Portal>
  )
}

const TelehealthTimer = () => {
  const { recordingDuration } = useTelehealthRecordingTimer()

  return (
    <Text
      pt="3px"
      ml="small"
      as="span"
      fontSize="12px"
      sx={{
        color: 'dark_gray',
        fontVariantNumeric: 'oldstyle-nums tabular-nums'
      }}
    >
      {formatTimeFromSeconds(recordingDuration)}
    </Text>
  )
}

const TimerManager = () => {
  const { isInCall } = usePatientDailyManager()

  const { beginRecordingTimer, stopRecordingTimer, resetRecordingTimer } =
    useTelehealthRecordingTimer()

  useEffect(() => {
    if (isInCall) {
      beginRecordingTimer()
    }
  }, [isInCall, beginRecordingTimer])

  useDailyEvent('error', event => {
    if (event.error.type === 'ejected') {
      stopRecordingTimer()
    }
  })

  useDailyEvent('left-meeting', () => {
    resetRecordingTimer()
  })

  return null
}
